/* External */
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

/* Storage */
import { selectAllChannels } from '../storage/channels';

/* Hooks */
import useFetchCarousel from './useFetchCarousel';
import useIsFetchingChannels from './useIsFetchingChannels';
import useCarouselContents from './useCarouselContents';
import useFilterChannelsForYou from './useFilterChannelsForYou';
import { Filter } from './useEpgFilters';

/* Utils */
import { EPG_FILTERS_ID, EPG_FILTERS_TYPES, RECENTLY_WATCHED_PARAMS } from '../utils/constants';

export default function useFilterChannels(filter, allFilters, {
  hideAllStationsFromGroupedChannels = false,
} = {}) {
  const { formatMessage } = useIntl();
  const { carousel, isFetching: isFetchingCarousel } = useFetchCarousel({
    sourceCarouselId: filter?.type === EPG_FILTERS_TYPES.SECTION ? filter?.id : '',
    type: filter?.id === RECENTLY_WATCHED_PARAMS.sourceCarouselId ? RECENTLY_WATCHED_PARAMS.type : '',
    source: filter?.id === RECENTLY_WATCHED_PARAMS.sourceCarouselId ? RECENTLY_WATCHED_PARAMS.source : '',
  });
  const sectionChannels = useCarouselContents(carousel);

  const {
    channels: forYouChannels,
    isFetching: isFetchingForYou,
  } = useFilterChannelsForYou(filter?.type === EPG_FILTERS_TYPES.FOR_YOU);

  const allChannels = useSelector(selectAllChannels);

  const channelsByCategory = useMemo(
    () => allChannels
      .filter((channel) => channel.categories?.includes(filter?.category || filter?.name)),
    [allChannels, filter],
  );

  const groupedChannelsByCategory = useMemo(() => {
    const list = [];

    if (!hideAllStationsFromGroupedChannels) {
      list.push(
        [[EPG_FILTERS_ID.ALL_STATIONS, Filter({
          id: EPG_FILTERS_ID.ALL_STATIONS,
          name: formatMessage({ id: 'epgFilters.all' }),
          type: EPG_FILTERS_TYPES.ALL,
          emptyMessages: {
            title: formatMessage({ id: 'epgEmptyStations.category.title' }),
            description: formatMessage({ id: 'epgEmptyStations.category.description' }),
          },
        })], sectionChannels.toMap()],
      );
    }

    list.push(
      [[EPG_FILTERS_ID.RECENTLY_WATCHED, Filter({
        id: EPG_FILTERS_ID.RECENTLY_WATCHED,
        name: formatMessage({ id: 'epgFilters.recentlyWatched' }),
        type: EPG_FILTERS_TYPES.SECTION,
        emptyMessages: {
          title: formatMessage({ id: 'epgEmptyStations.recentlyWatched.title' }),
          description: formatMessage({ id: 'epgEmptyStations.recentlyWatched.description' }),
        },
      })], sectionChannels.toMap()],
    );

    for (const filterData of allFilters) {
      const filteredChannels = allChannels
        .filter((channel) => channel.categories?.includes(
          filterData?.[1]?.category || filterData?.[1]?.name || filterData?.[0],
        ) && channel.isEntitled);

      if (filteredChannels.size > 0) {
        list.push([filterData, filteredChannels]);
      }
    }

    return list;
  }, [allChannels, allFilters, formatMessage, hideAllStationsFromGroupedChannels, sectionChannels]);

  const isFetchingChannels = useIsFetchingChannels();

  switch (filter?.type) {
    case EPG_FILTERS_TYPES.ALL: {
      return {
        channels: allChannels,
        isFetching: isFetchingChannels,
        groupedChannels: groupedChannelsByCategory,
      };
    }
    case EPG_FILTERS_TYPES.CATEGORY: {
      return {
        channels: channelsByCategory,
        isFetching: isFetchingChannels,
        groupedChannels: groupedChannelsByCategory,
      };
    }
    case EPG_FILTERS_TYPES.SECTION: {
      return {
        channels: sectionChannels,
        isFetching: isFetchingCarousel,
        groupedChannels: groupedChannelsByCategory,
      };
    }
    case EPG_FILTERS_TYPES.FOR_YOU: {
      return {
        channels: forYouChannels,
        isFetching: isFetchingForYou,
        groupedChannels: groupedChannelsByCategory,
      };
    }
    default: {
      return { isFetching: true };
    }
  }
}
